// External libraries
import { useCallback, useEffect, useState } from "react";

// FontAwesome icons
import { faFolder, faWindowRestore } from "@fortawesome/pro-duotone-svg-icons";
import ColorChangingIcon from "../../../Common/UI/ColorChangingIcon";

// Kendo React components
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { ListBox, ListBoxToolbar, processListBoxData } from "@progress/kendo-react-listbox";

// Common signals
import { allTemplates, userDatasets } from "../../../Common/Store/CommonSignals";

// Homepage signals
import { uniqueTemplateTags } from "../HomepageSignals";

// TreeView types
export const TREE_VIEW_TYPES = {
  favorites: {
    id: "favorites",
    label: "Favorites",
    description: "Display your favorite templates and datasets",
  },
  myTemplates: {
    id: "myTemplates",
    label: "My Templates",
    description: "Display templates where you are Owner or Co-Owner",
  },
  tagged: {
    id: "tagged",
    label: "Tagged",
    description: "Display templates and datasets with a specific tag",
  },
  custom: {
    id: "custom",
    label: "Custom",
    description: "Select specific templates or datasets to display",
  },
};

const SELECTED_FIELD = "selected";

// Helper function to deselect all items in a list
const deselectAllItems = (data) => data.map((item) => ({ ...item, [SELECTED_FIELD]: false }));

// Custom item renderer with template/dataset icons
const ItemRender = (props) => {
  const { dataItem } = props;
  const isDataset = dataItem.type === "dataset";

  return (
    <span className="treeview-item-render">
      <ColorChangingIcon
        icon={isDataset ? faFolder : faWindowRestore}
        initialPrimaryColor={isDataset ? "#FFD700" : "#4a90d9"}
        initialSecondaryColor={isDataset ? "#FFA500" : "#2e5a87"}
        initialSize={16}
        hoverSize={16}
      />
      <span className="treeview-item-name">{dataItem.name}</span>
    </span>
  );
};

// Custom list item wrapper to add selection functionality
const CustomListItem = ({ children, dataItem, selected, onItemClick }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onItemClick) {
      onItemClick({ dataItem });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (onItemClick) {
        onItemClick({ dataItem });
      }
    }
  };

  return (
    <div
      className={`k-list-item treeview-custom-list-item ${selected ? "k-selected" : ""}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="option"
      aria-selected={selected}
    >
      {children}
    </div>
  );
};

function TreeViewConfigDialog({ onClose, onConfirm, onDelete, treeViewConfig, tileId }) {
  const [name, setName] = useState(treeViewConfig?.name || "New TreeView");
  const [type, setType] = useState(treeViewConfig?.type || "favorites");
  const [tagName, setTagName] = useState(treeViewConfig?.tagName || "");
  const [selectedItems, setSelectedItems] = useState(treeViewConfig?.treeItemIds || []);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItemsList, setSelectedItemsList] = useState([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (treeViewConfig) {
      setName(treeViewConfig.name || "New TreeView");
      setType(treeViewConfig.type || "favorites");
      setTagName(treeViewConfig.tagName || "");
      setSelectedItems(treeViewConfig.treeItemIds || []);
    }
  }, [treeViewConfig]);

  // Initialize ListBox data when type changes to custom
  useEffect(() => {
    if (type === "custom") {
      // Combine templates and datasets into a single list
      const templateOptions = allTemplates.value.map((t) => ({
        id: t.id,
        name: t.templateName,
        type: "template",
        [SELECTED_FIELD]: false,
      }));

      const datasetOptions = (userDatasets.value || []).map((d) => ({
        id: d.id,
        name: d.datasetName,
        type: "dataset",
        [SELECTED_FIELD]: false,
      }));

      const allOptions = [...templateOptions, ...datasetOptions];
      const selectedIds = new Set(selectedItems);
      const selected = allOptions.filter((item) => selectedIds.has(item.id));
      const available = allOptions.filter((item) => !selectedIds.has(item.id));

      setAvailableItems(available);
      setSelectedItemsList(selected);
    }
  }, [type, selectedItems]);

  const handleConfirm = () => {
    const finalItemIds = type === "custom" ? selectedItemsList.map((item) => item.id) : [];
    onConfirm(tileId, treeViewConfig.id, {
      name,
      type,
      tagName: type === "tagged" ? tagName : undefined,
      treeItemIds: finalItemIds,
    });
  };

  const handleDelete = () => {
    onDelete(tileId, treeViewConfig.id);
  };

  // Toggle selection state of clicked item
  const handleItemClick = useCallback((event, data, setData) => {
    const updatedData = data.map((item) =>
      item.id === event.dataItem.id ? { ...item, [SELECTED_FIELD]: !item[SELECTED_FIELD] } : item
    );
    setData(updatedData);
  }, []);

  // Handle toolbar button clicks (transfer items between lists)
  const handleToolBarClick = useCallback(
    (e) => {
      const result = processListBoxData(
        availableItems,
        selectedItemsList,
        e.toolName || "",
        SELECTED_FIELD
      );

      setAvailableItems(deselectAllItems(result.listBoxOneData));
      setSelectedItemsList(deselectAllItems(result.listBoxTwoData));
    },
    [availableItems, selectedItemsList]
  );

  // Render item wrapper para ListBox
  const renderItem = useCallback(
    (props, listData, setListData) => {
      const { dataItem } = props;
      const isSelected = dataItem[SELECTED_FIELD];

      return (
        <CustomListItem
          dataItem={dataItem}
          selected={isSelected}
          onItemClick={(e) => handleItemClick(e, listData, setListData)}
        >
          <ItemRender {...props} />
        </CustomListItem>
      );
    },
    [handleItemClick]
  );

  // Toolbar component for ListBox
  const toolbarComponent = useCallback(
    () => (
      <ListBoxToolbar
        tools={["transferTo", "transferFrom", "transferAllTo", "transferAllFrom"]}
        data={availableItems}
        dataConnected={selectedItemsList}
        onToolClick={handleToolBarClick}
      />
    ),
    [availableItems, selectedItemsList, handleToolBarClick]
  );

  return (
    <Dialog
      title="Configure TreeView"
      onClose={onClose}
      width={700}
    >
      <div className="treeview-dialog-content">
        <div className="treeview-dialog-section">
          <label className="treeview-dialog-label">TreeView Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.value)}
            className="treeview-dialog-input-full"
          />
        </div>

        <div className="treeview-dialog-section">
          <label className="treeview-dialog-label">TreeView Type</label>
          <div className="treeview-type-options">
            {Object.values(TREE_VIEW_TYPES).map((tvType) => (
              <div
                key={tvType.id}
                className={`treeview-type-option ${type === tvType.id ? "selected" : ""}`}
                onClick={() => setType(tvType.id)}
              >
                <input
                  type="radio"
                  checked={type === tvType.id}
                  onChange={() => setType(tvType.id)}
                />
                <div>
                  <div className="treeview-type-label">{tvType.label}</div>
                  <div className="treeview-type-description">{tvType.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {type === "tagged" && (
          <div className="treeview-dialog-section">
            <label className="treeview-dialog-label">Tag Name</label>
            <AutoComplete
              data={uniqueTemplateTags.value}
              value={tagName}
              onChange={(e) => setTagName(e.value)}
              placeholder="Select or enter a tag..."
              className="treeview-dialog-input-full"
              clearButton
            />
          </div>
        )}

        {type === "custom" && (
          <div className="treeview-custom-section">
            <div className="treeview-search-container">
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.value || "")}
                placeholder="Search templates and datasets..."
                className="treeview-dialog-input-full"
              />
            </div>
            <div className="treeview-listbox-container">
              <div className="treeview-listbox-column">
                <div className="treeview-listbox-header">Available Items</div>
                <ListBox
                  data={availableItems.filter((item) =>
                    item.name.toLowerCase().includes(searchText.toLowerCase())
                  )}
                  textField="name"
                  selectedField={SELECTED_FIELD}
                  item={(props) => renderItem(props, availableItems, setAvailableItems)}
                  className="treeview-listbox"
                  toolbar={toolbarComponent}
                />
              </div>

              <div className="treeview-listbox-column">
                <div className="treeview-listbox-header">Selected Items</div>
                <ListBox
                  data={selectedItemsList}
                  textField="name"
                  selectedField={SELECTED_FIELD}
                  item={(props) => renderItem(props, selectedItemsList, setSelectedItemsList)}
                  className="treeview-listbox-selected"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <DialogActionsBar>
        <Button
          className="k-button"
          themeColor="error"
          onClick={handleDelete}
        >
          Delete TreeView
        </Button>
        <div className="treeview-dialog-spacer" />
        <Button
          className="k-button"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          className="k-button"
          themeColor="primary"
          onClick={handleConfirm}
        >
          Save
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
}

export default TreeViewConfigDialog;
